import { useEffect } from "react";

const VISIBLE_CLASS = "is-visible";

/**
 * Reveals all [data-reveal] elements as soon as they enter the viewport.
 *
 * Besides the IntersectionObserver this also keeps a plain geometric check
 * on scroll / resize. Some browsers stop reporting intersections when the
 * scroll container, lazy sections or the document height change while
 * scrolling - which is exactly how elements end up "stuck" invisible until
 * the user scrolls up and down again.
 */
export function useScrollReveal() {
  useEffect(() => {
    const pending = new Set<HTMLElement>();
    const observed = new WeakSet<HTMLElement>();
    let frame = 0;

    const reveal = (el: HTMLElement) => {
      const delay = el.dataset.delay ? parseFloat(el.dataset.delay) : 0;
      el.style.setProperty("--reveal-delay", `${delay * 0.12}s`);
      el.classList.add(VISIBLE_CLASS);
      pending.delete(el);
    };

    const isInView = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      if (viewportHeight === 0) return false;
      // Visible as soon as any part of it sits inside the upper 95% of the screen.
      return rect.bottom > 0 && rect.top < viewportHeight * 0.95;
    };

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const el = entry.target as HTMLElement;
                reveal(el);
                observer?.unobserve(el);
              });
            },
            { threshold: 0.01, rootMargin: "0px 0px -5% 0px" }
          )
        : null;

    const collect = () => {
      document.querySelectorAll<HTMLElement>("[data-reveal]").forEach((el) => {
        if (el.classList.contains(VISIBLE_CLASS)) return;
        pending.add(el);
        if (observer && !observed.has(el)) {
          observed.add(el);
          observer.observe(el);
        }
      });
    };

    const flush = () => {
      frame = 0;
      pending.forEach((el) => {
        if (!el.isConnected) {
          pending.delete(el);
          return;
        }
        if (isInView(el)) reveal(el);
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(flush);
    };

    collect();
    schedule();

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    const mutations =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            collect();
            schedule();
          })
        : null;
    mutations?.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      mutations?.disconnect();
      observer?.disconnect();
    };
  }, []);
}